import { effect, inject, Injectable, linkedSignal, Signal } from '@angular/core';
import { __ } from '../../functions/object.functions';
import { LOCAL_STORAGE } from '../../tokens/local-storage.token';

const REDIRECT_STORAGE_KEY = `ambos_immo_redirect_uri_qzweuiaijsdoasdasd`;

@Injectable({ providedIn: 'root' })
export class RedirectService {

    // -----------------------------------------------------------------------------------------------------
    // @ DEPENDENCIES
    // -----------------------------------------------------------------------------------------------------

    private readonly localStorage: Storage | null = inject(LOCAL_STORAGE);

    // -----------------------------------------------------------------------------------------------------
    // @ PRIVATE INSTANCE PROPERTIES
    // -----------------------------------------------------------------------------------------------------

    private _redirectUri = linkedSignal<string | undefined>(() => {
        const storedUri = this.localStorage?.getItem(REDIRECT_STORAGE_KEY);

        try {
            return __.IsNullOrUndefinedOrEmpty(storedUri) ? undefined : storedUri!;
        } catch (error) {
            return undefined;
        }
    });

    // -----------------------------------------------------------------------------------------------------
    // @ PUBLIC INSTANCE PROPERTIES
    // -----------------------------------------------------------------------------------------------------

    redirectUri: Signal<string | undefined> = this._redirectUri.asReadonly();

    // -----------------------------------------------------------------------------------------------------
    // @ EFFECTS
    // -----------------------------------------------------------------------------------------------------

    syncRedirectUriEffect = effect(() => {
        const uri: string | undefined = this._redirectUri();

        if (__.IsNullOrUndefinedOrEmpty(uri)) {
            return this.localStorage?.removeItem(REDIRECT_STORAGE_KEY);
        }

        this.localStorage?.setItem(REDIRECT_STORAGE_KEY, uri!);
    });

    // -----------------------------------------------------------------------------------------------------
    // @ PUBLIC METHODS
    // -----------------------------------------------------------------------------------------------------

    /**
     * Sets the redirect URI. This can be used to redirect the user to a specific page after a successful login or other action.
     * 
     * @param redirectUri - The redirect URI to set. This should be a valid URI that the user can be redirected to after a successful login or other action.
     * @return {void}
     */
    setRedirectUri(redirectUri: string): void {
        // Only if no redirect uri is currently present we can set a new one
        if (__.IsNullOrUndefinedOrEmpty(this._redirectUri()) || this._redirectUri() === '/') {
            this._redirectUri.set(redirectUri);
        }
    }

    /**
     * Resets the redirect URI. This can be used to clear the redirect URI after a successful login or other action, so that the user is not redirected to an unintended page in the future.
     * 
     * @return {void}
     */
    reset(): void {
        this._redirectUri.set(undefined);
    }
}
